import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mdiAccount, mdiClose, mdiMagnifyPlusOutline, mdiUpload } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle, DialogClose } from './ui/dialog';
import { cn } from '../lib/utils';
import { PictureApiService } from '../lib/pictureApiService';
import { FileValidator, DEFAULT_IMAGE_VALIDATION_RULES } from '../lib/fileValidator';
import { SECTION_ICONS } from '../lib/section-icons'

export interface EntityPictureProps {
  entityType: 'organization' | 'asset' | 'user';
  entityId: string;
  orgCode: string;
  size?: string; // e.g., '128x128', '256x256', '512x512'
  documentUid?: string; // Document UID for cache busting - use when available
  editable?: boolean;
  fallbackImage?: string;
  /** An MDI path from `@mdi/js`. */
  fallbackIcon?: string;
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
  onClick?: () => void;
  onUpload?: (success: boolean, error?: string) => void;
  showUploadButton?: boolean;
  uploadButtonText?: string;
  maxFileSize?: number; // in MB
  acceptedFormats?: string[];
}

const DEFAULT_FALLBACK_ICONS = {
  organization: SECTION_ICONS.organizations,
  asset: SECTION_ICONS.assets,
  user: mdiAccount,
};

export const EntityPicture: React.FC<EntityPictureProps> = ({
  entityType,
  entityId,
  orgCode,
  size,
  documentUid,
  editable = false,
  fallbackImage,
  fallbackIcon,
  className,
  style,
  alt,
  onClick,
  onUpload,
  showUploadButton = false,
  uploadButtonText,
  maxFileSize = 10,
  acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'],
}) => {
  const { t } = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentDocumentUid, setCurrentDocumentUid] = useState<string | undefined>(documentUid);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fallbackIconPath = fallbackIcon || DEFAULT_FALLBACK_ICONS[entityType];

  // Update currentDocumentUid when prop changes
  React.useEffect(() => {
    setCurrentDocumentUid(documentUid);
    setImageError(false); // Reset image error when documentUid changes
  }, [documentUid]);

  const imageUrl = PictureApiService.getPictureUrl(entityType, orgCode, entityId, size, currentDocumentUid);

  const handleFileSelect = async (file: File) => {
    // Validate file
    const validationRules = {
      ...DEFAULT_IMAGE_VALIDATION_RULES,
      maxSizeBytes: maxFileSize * 1024 * 1024,
      allowedTypes: acceptedFormats,
    };

    const validationError = await FileValidator.validateImageFile(file, validationRules);
    if (validationError) {
      setError(validationError);
      onUpload?.(false, validationError);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const uploadResult = await PictureApiService.uploadPicture(
        entityType,
        orgCode,
        entityId,
        file,
        (progress) => setUploadProgress(progress)
      );

      setIsUploading(false);
      setUploadProgress(100);
      setCurrentDocumentUid(uploadResult.document_uid);
      setImageError(false); // Reset image error after successful upload
      onUpload?.(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('picture.upload_failed');
      setError(errorMessage);
      setIsUploading(false);
      setUploadProgress(0);
      onUpload?.(false, errorMessage);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (editable) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set drag over to false if we're leaving the component entirely
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (!editable) return;

    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (currentDocumentUid && !imageError) {
      // If there's an image, show the modal
      setIsModalOpen(true);
    } else if (editable && !isUploading) {
      // If no image and editable, open file selector
      fileInputRef.current?.click();
    }
  };

  const handleUploadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleDeleteClick = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!confirm('Are you sure you want to remove this picture? This action cannot be undone.')) {
      return;
    }

    setError(null);

    try {
      await PictureApiService.deletePicture(entityType, orgCode, entityId);
      setCurrentDocumentUid(undefined);
      setImageError(false);
      onUpload?.(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('picture.delete_failed');
      setError(errorMessage);
      onUpload?.(false, errorMessage);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const clearError = () => {
    setError(null);
  };

  const renderContent = () => {
    // Show image if we have a documentUid and no image error
    if (currentDocumentUid && !imageError) {
      return (
        <div className="relative w-full h-full group cursor-pointer">
          <img
            src={imageUrl}
            alt={alt || `${entityType} picture`}
            className="w-full h-full object-cover"
            style={{ aspectRatio: '1' }}
            onError={handleImageError}
          />
          {/* Zoom icon overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <AppIcon path={mdiMagnifyPlusOutline} size="xl" className="text-white drop-shadow-lg" />
            </div>
          </div>
          {/* Discrete action buttons in corner */}
          {editable && (
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleUploadClick}
                className="h-8 w-8 p-0 bg-white/90 hover:bg-white text-black shadow-sm"
                title={t('picture.upload_new')}
              >
                <AppIcon path={mdiUpload} size="xs" />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDeleteClick}
                className="h-8 w-8 p-0 bg-red-500/90 hover:bg-red-500 shadow-sm"
                title={t('picture.remove')}
              >
                <AppIcon path={mdiClose} size="xs" />
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Fallback content
    return (
      <div className="flex flex-col items-center justify-center w-full h-full text-muted-foreground">
        {fallbackImage ? (
          <img
            src={fallbackImage}
            alt={alt || `${entityType} fallback`}
            className="w-full h-full object-cover opacity-50"
          />
        ) : (
          <AppIcon path={fallbackIconPath} size="2xl" className="opacity-50" />
        )}
        {editable && (
          <div className="mt-2 text-xs text-center">
            {t('picture.click_or_drag')}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <div
        className={cn(
          "relative border-2 border-dashed border-muted-foreground/25 rounded-lg overflow-hidden",
          "transition-all duration-200",
          editable && "cursor-pointer hover:border-muted-foreground/50",
          isDragOver && "border-primary bg-primary/5",
          isUploading && "opacity-75",
          className
        )}
        style={style}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {renderContent()}

        {isUploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="mb-2">
                <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
              </div>
              <div className="text-sm">
                Uploading... {uploadProgress}%
              </div>
              <div className="w-32 bg-white/20 rounded-full h-2 mt-2">
                <div
                  className="bg-white h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-2 rounded border border-destructive/20">
          {error}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearError}
            className="ml-2 h-auto p-0 text-destructive hover:text-destructive/80"
          >
            <AppIcon path={mdiClose} size="xs" />
          </Button>
        </div>
      )}

      {/* Upload button */}
      {showUploadButton && editable && !isUploading && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
        >
          <AppIcon path={mdiUpload} size="sm" className="mr-2" />
          {uploadButtonText || t('picture.upload')}
        </Button>
      )}

      {/* Hidden file input */}
      {editable && (
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats?.join(',') || 'image/*'}
          onChange={handleFileInputChange}
          className="hidden"
        />
      )}

      {/* Picture view modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-2 bg-black/95 border-none">
          <DialogTitle className="sr-only">
            {alt || `${entityType} picture`}
          </DialogTitle>
          <div className="relative flex items-center justify-center min-h-[50vh]">
            <img
              src={PictureApiService.getPictureUrl(entityType, orgCode, entityId, undefined, currentDocumentUid)}
              alt={alt || `${entityType} picture`}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <DialogClose className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <AppIcon path={mdiClose} size="lg" className="text-white" />
              <span className="sr-only">Close</span>
            </DialogClose>
            {/* Action buttons in modal */}
            {editable && (
              <div className="absolute bottom-4 right-4 flex gap-2">
                <Button
                  variant="secondary"
                  onClick={handleUploadClick}
                  className="bg-white/90 hover:bg-white text-black"
                >
                  <AppIcon path={mdiUpload} size="sm" className="mr-2" />
                  Upload New
                </Button>
                <Button
                  variant="destructive"
                  onClick={(e) => {
                    handleDeleteClick(e);
                    setIsModalOpen(false);
                  }}
                  className="bg-red-500/90 hover:bg-red-500"
                >
                  <AppIcon path={mdiClose} size="sm" className="mr-2" />
                  Remove
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};